import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus trap for the one modal surface a shell owns: the ConsoleShell mobile
 * nav drawer.
 *
 * A drawer that does not trap focus is a drawer a keyboard user tabs straight
 * out of into the page it is covering, which is worse than no drawer at all.
 * So this does the four things the pattern needs and nothing else:
 *
 *   1. move focus into the panel on open,
 *   2. keep Tab and Shift+Tab inside it,
 *   3. close on Escape,
 *   4. return focus to whatever opened it.
 *
 * It also locks the page behind, using the design system's own
 * `.dsk-scroll-locked` class rather than an inline style, so the lock is
 * themeable and greppable like everything else.
 *
 * @param {{ current: HTMLElement | null }} ref  the panel
 * @param {boolean} active
 * @param {() => void} onClose
 */
export function useFocusTrap(ref, active, onClose) {
  useEffect(() => {
    if (!active) return undefined;

    const panel = ref.current;
    if (!panel) return undefined;

    const opener = document.activeElement;
    const body = document.body;

    body.classList.add('dsk-scroll-locked');

    // The panel itself is focusable via tabIndex={-1}, so this always lands
    // somewhere sensible even when the drawer holds no controls yet.
    const first = panel.querySelector(FOCUSABLE);
    (first || panel).focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === panel
      );
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const head = items[0];
      const tail = items[items.length - 1];

      if (event.shiftKey && (document.activeElement === head || document.activeElement === panel)) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    };

    panel.addEventListener('keydown', onKeyDown);

    return () => {
      panel.removeEventListener('keydown', onKeyDown);
      body.classList.remove('dsk-scroll-locked');
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [ref, active, onClose]);
}

export default useFocusTrap;
